import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { FlightLookupFormComponent } from './flight-lookup-form/flight-lookup-form.component';
import { FlightSearchFormComponent } from './flight-search-form/flight-search-form.component';
import { FLIGHT_LOOKUP_FORM, PAYMENT_NOTIFICATION_MESSAGE } from 'src/app/_constants/app.constants';
import { ObjectLiteral } from 'src/app/_models/user';
import { RtcService } from 'src/app/_services/sdk/rtc/rtc.service';
import { FlightService } from 'src/app/_services/api/flight/flight.service';
import { LanguageService } from 'src/app/_services/api/language/language.service';
import { LoggerService } from 'src/app/_services/ui/logger/logger.service';
import { LoadingService } from 'src/app/_services/ui/loading/loading.service';
import { SessionService } from 'src/app/_services/session/session.service'; 
import { WelcomeService } from './_services/welcome.service';

@Component({
  selector: 'ua-welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.scss']
})
export class WelcomeComponent implements OnInit {

  @ViewChild(FlightLookupFormComponent) flightLookupFormComponent!: FlightLookupFormComponent;
  @ViewChild(FlightSearchFormComponent) flightSearchFormComponent!: FlightSearchFormComponent;

  isLoading: boolean = false;
  // isTabbedView: boolean = true;
  errorMessage: any;
  showErrorMessage: boolean = false;
  pageConstants: ObjectLiteral;
  commonConstants: ObjectLiteral;
  preChatConstants: ObjectLiteral;
  languageSubscription: Subscription;
  paymentMessage = PAYMENT_NOTIFICATION_MESSAGE;
  info :string = "";
  linkText: any;
  isScanningInProgress = false;
  showBoardingPassScan: boolean = false;
  showFlightLookupForm: boolean = false;
  showBackButton: boolean = false;
  showErrorScanTimeout: boolean = false;

  showErrorScanTimeoutSubscription: Subscription;
  subscribeShowErrorScanTimeout() {
    this.showErrorScanTimeoutSubscription = this.welcomeService.showScanTimeout.subscribe((stream:{action:string, data:any}) => {
      if (stream.action === 'open') {
        this.showErrorScanTimeout = true;
      }

      if (stream.action === 'close') {
        this.showErrorScanTimeout = false;
      }
    })
  }
  hideErrorScanTimeout() {
    this.showErrorScanTimeout = false;
  }

  showBackButtonSubscription: Subscription;
  subscribeShowBackButton() {
    this.showBackButtonSubscription = this.welcomeService.showBackButtonSubject.subscribe((stream:{action:string, data:any}) => {
      if (stream.action === 'assign') this.showBackButton = stream.data;
    })
  }

  showFlightLookupFormSubscription: Subscription;
  subscribeShowFlightLookupForm() {
    this.showFlightLookupFormSubscription = this.welcomeService.showFlightLookupFormSubject.subscribe((stream:{action:string, data:any}) => {
      if (stream.action === 'assign') this.showFlightLookupForm = stream.data;
    })
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private logger: LoggerService,
    private sessionService: SessionService,
    private flightService: FlightService,
    private loadingService: LoadingService,
    private languageService: LanguageService,
    private rtcService: RtcService,
    private welcomeService: WelcomeService,
  ) {
    this.languageSubscription = this.languageService.languageChanges.subscribe(() => {
      this.pageConstants = this.languageService.constants?.home || {};
      this.commonConstants = this.languageService.constants?.commons || {}; 
      this.preChatConstants = this.languageService.constants?.preChat || {}; 
    })
  }

  async ngOnInit(): Promise<void> {
    this.logger.debug("WelcomeComponent.ngOnInit()", this);
    this.flightService.isHelpRequested = false;
    this.flightService.hasTrhNavigation = false;
    this.flightService.clearFlightDetails();
    this.listenToLoading();
    await this.checkDeviceRtc();
    if (this.sessionService.showFlightLookupForm) {
      this.showBackButton = true;
      this.showFlightLookupForm = true;
      if (this.sessionService.userData.lastName) {
        this.flightLookupFormComponent.flightLookupForm.patchValue({ lastName: this.sessionService.userData.lastName });
      }
    }
    
    this.subscribeShowBackButton();
    this.subscribeShowFlightLookupForm();
    this.subscribeShowErrorScanTimeout();
  }

  async checkDeviceRtc() {
    this.logger.debug("WelcomeComponent.checkDeviceRtc()", this);
    const isCameraAvailable = await this.rtcService.isCameraAvailable();
    const isMobileDevice = await this.rtcService.isMobileDevice();
    const isBrowserSupported = this.rtcService.isBrowserSupported()
    this.showBoardingPassScan = isCameraAvailable && isMobileDevice && isBrowserSupported
    this.linkText = this.showBoardingPassScan ? FLIGHT_LOOKUP_FORM.MESSAGES.SCAN_LINK_TEXT : FLIGHT_LOOKUP_FORM.MESSAGES.LINK_TEXT;
    this.info =
      this.showBoardingPassScan
        ? FLIGHT_LOOKUP_FORM.MESSAGES.INFO
        : FLIGHT_LOOKUP_FORM.MESSAGES.INFO_NO_SCAN;
  }

  isSearchError(text: string) {
    this.logger.debug("WelcomeComponent.isSearchError()", this);
    this.errorMessage = text;
    this.showErrorMessage = text ? true : false;
  }
  
  /**
 * Listen to the loadingSub property in the LoadingService class. This drives the
 * display of the loading spinner.
 */
  listenToLoading(): void {
    this.logger.debug("WelcomeComponent.listenToLoading()", this);
    this.loadingService.loadingSub
      .pipe(delay(0)) // This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
      .subscribe(({ loading }) => {
        this.isLoading = loading;
      });
  }

  openErrorPopup(error: string) {
    this.logger.debug("WelcomeComponent.openErrorPopup()", this);
    this.errorMessage = !error ? FLIGHT_LOOKUP_FORM.MESSAGES.SCAN_TIMEOUT : error;
    this.showErrorMessage = true;
    this.document.defaultView.scrollTo(0, 0)
  }

  async startScanning(){
    this.logger.debug("WelcomeComponent.startScanning()", this)
    if (this.rtcService.browser.isSafari && this.rtcService.browser.version >= 16) {
      let permissionStatus = await this.rtcService.requestPermissions('camera').catch(e => e)
      if (this.rtcService.permissionsRequested && permissionStatus.state !== 'granted') {
        this.openErrorPopup('')
      } else {
        this.isScanningInProgress = true
      }
    } else {
      this.isScanningInProgress = true;
    }
  }

  onScannerClose(){
    this.logger.debug("WelcomeComponent.onScannerClose()", this);
    this.isScanningInProgress = false;
    this.showErrorMessage = false;
  }

  onScannerResult(data){
    this.logger.debug("WelcomeComponent.onScannerResult()", this, data);
    this.isScanningInProgress = false;
    if(data == null){
      this.openErrorPopup(FLIGHT_LOOKUP_FORM.MESSAGES.SCAN_TIMEOUT);
    }
    else{
      this.showErrorMessage = false;
      const {pnr, lastName} = data;
      this.showBackButton = true;
      this.showFlightLookupForm = true;
      this.sessionService.showFlightLookupForm = true;
      this.flightLookupFormComponent.flightLookupForm.patchValue({
        pnr: pnr,
        lastName: lastName
      });
      this.flightLookupFormComponent.onFormSubmit();
    }
  }

  onScannerTimeout(){
    this.logger.debug("WelcomeComponent.onScannerTimeout()", this);
    this.isScanningInProgress = false;
    this.openErrorPopup(FLIGHT_LOOKUP_FORM.MESSAGES.SCAN_TIMEOUT);
  }


  goBack() {
    this.logger.debug("WelcomeComponent.goBack()", this);
    this.showBackButton = false;
    this.showErrorMessage = false;
    this.showFlightLookupForm = false;
  }

  ngOnDestroy() {
    if (this.showBackButtonSubscription) this.showBackButtonSubscription.unsubscribe();
    if (this.showErrorScanTimeoutSubscription) this.showErrorScanTimeoutSubscription.unsubscribe();
    if (this.showFlightLookupFormSubscription) this.showFlightLookupFormSubscription.unsubscribe();
  }
}